import { savePuzzleProgress } from '../../../utils/persistenceUtils';
import { PathwordData, Position, PuzzleNames, STATS_DIALOG_DELAY } from '../../../utils/types';
import { getZeroMatrix, PathwordStore } from './PathwordStore';

// Maps the number of default hints to which hint indices should be enabled
// For 4x4 Pathword: 0 hints = [], 1 hint = [0], 2 hints = [0, 2]
export const getEnabledHintsForPathword = (numHints: number): number[] => {
  const hintMaps: { [key: number]: number[] } = {
    0: [],
    1: [0],
    2: [0, 2],
  };
  return hintMaps[numHints] ?? [0, 2]; // default to 2 if invalid
};

// Maps the number of default hints to which hint indices should be enabled
// For 5x5 Pathword: 0 hints = [], 1 hint = [0], 2 hints = [0, 2], 3 hints = [0, 2, 4], 4 hints = [0, 2, 4, 6]
export const getEnabledHintsForPathword5x5 = (numHints: number): number[] => {
  const hintMaps: { [key: number]: number[] } = {
    0: [],
    1: [0],
    2: [0, 2],
    3: [0, 2, 4],
    4: [0, 2, 4, 6],
  };
  return hintMaps[numHints] ?? [0, 2, 4, 6]; // default to 4 if invalid
};


// The frontend utils expect a matrix that has numbered cells to describe the solution path.
// So we convert the path from a sequential array of positions, to that other format.
export const generateNumberedMatrixFromPositionPath = (path: [number, number][]) => {
  // Find the matrix dimensions
  const maxRow = Math.max(...path.map(([r, _]) => r));
  const maxCol = Math.max(...path.map(([_, c]) => c));

  // Initialize matrix with zeros
  const matrix: number[][] = Array.from({ length: maxRow + 1 }, () => Array(maxCol + 1).fill(0));

  // Fill the matrix with the step numbers
  path.forEach(([row, col], index) => {
    matrix[row][col] = index + 1;
  });

  return matrix;
};

// Create a map of grid positions to hint letter indices for quick lookup
// Returns object like { "0_1": 3, "2_3": 7 } where key is "row_col" and value is hintLetter index
export const getHintPositions = (solutionPath: Position[], hintLetters: number[]): {[key: string]: number} => {
  const hintPositions: {[key: string]: number} = {};

  hintLetters.forEach(hintIndex => {
    // solutionPath is 0-indexed array, so position at index hintIndex is what we want
    const [row, col] = solutionPath[hintIndex];
    hintPositions[`${row}_${col}`] = hintIndex;
  });

  return hintPositions;
};

export const directions = [
  [-1, -1], [-1, 0], [-1, 1],
  [0, -1],           [0, 1],
  [1, -1],  [1, 0],  [1, 1]
];

export const initPathword = (puzzleData: PathwordData, numDefaultHints: number = 2) => {
  PathwordStore.update(s => {
    s.puzzleId = puzzleData.puzzleId;
    s.cluetext = puzzleData.cluetext;
    s.vowelMatrix = puzzleData.vowelMatrix;
    s.solutionPath = generateNumberedMatrixFromPositionPath(puzzleData.solutionPath);
    s.solutionLines = getLineOrientationsForPath(s.solutionPath);
    s.hintLetters = puzzleData.hintLetters;
    s.hintPositions = getHintPositions(puzzleData.solutionPath, puzzleData.hintLetters);
    s.enabledHints = getEnabledHintsForPathword(numDefaultHints);
    s.initialEnabledHints = numDefaultHints;
  });
};

export const onReveal = () => {
  PathwordStore.update(s => {
    clearInterval(s.timerUpdatingFn);
    s.highlights = getZeroMatrix(4);
    s.revealed = true;
    s.isPaused = false;

    savePuzzleProgress(PuzzleNames.Pathword, {
      puzzleId: s.puzzleId,
      revealed: true,
      solved: false,
      time: s.time,
    });
  });
};

export const onSolve = () => {
  const { time, timerUpdatingFn, puzzleId } = PathwordStore.getRawState();

  // This puzzle has a lengthy success animation, and we delay setting solved=true
  // until after it's done, in the setTimeout below.
  // Since the pause functionality doesn't stop the timer until the puzzle is marked solved,
  // we stop it here immediately as a courtesy, before the success animation finishes.
  clearInterval(timerUpdatingFn);
  
  setTimeout(() => {
    PathwordStore.update(s => {
      s.statsDialogIsOpen = true;
      s.solved = true;
    });
  }, STATS_DIALOG_DELAY);
  
  savePuzzleProgress(PuzzleNames.Pathword, {
    puzzleId,
    revealed: false,
    solved: true,
    time,
  });
};

export const savePathword = () => {
  const { solved, revealed, time, puzzleId } = PathwordStore.getRawState();

  if (solved || revealed) return;

  // just updating progress on pause or navigate away
  savePuzzleProgress(PuzzleNames.Pathword, {
    puzzleId,
    revealed,
    solved,
    time,
  });
};

// We just treat Y as a vowel for simplicity of the puzzle
export const isLetterAVowel = (letter: string) => {
  return 'aeiouy'.includes(letter);
};

const getRelativePositionOfCells = (
  currentCell: Position,
  otherCell: Position
): string => {
  const [rowCurr, colCurr] = currentCell;
  const [rowOther, colOther] = otherCell;

  if (rowOther < rowCurr) {
    if (colOther < colCurr) return 'topLeft';
    if (colOther > colCurr) return 'topRight';
    return 'top';
  }

  if (rowOther > rowCurr) {
    if (colOther < colCurr) return 'bottomLeft';
    if (colOther > colCurr) return 'bottomRight';
    return 'bottom';
  }

  if (colOther < colCurr) return 'left';
  if (colOther > colCurr) return 'right';

  // This case should not be reached if the otherCell is not the same as currentCell
  return '';
};

const getClassesForCell = (
  currentCell: Position,
  previousCell: Position | null,
  nextCell: Position | null,
): string => {
  let classList = '';

  if (previousCell) {
    const relativePosition = getRelativePositionOfCells(currentCell, previousCell);
    classList += `${relativePosition}B `;
  }

  if (nextCell) {
    const relativePosition = getRelativePositionOfCells(currentCell, nextCell);
    classList += `${relativePosition}A`;
  }

  return classList.trim();
};

// return the path orientation for the cells in the path, to draw the lines connecting them
export const getLineOrientationsForPath = (path: number[][]) => {
  let pathIndexLocations: { [cellValue: number]: Position } = {};
  let highestIndex = -Infinity;

  for (let row = 0; row < path.length; row++) {
    for (let col = 0; col < path[row].length; col++) {
      const cellValue = path[row][col];
      if (cellValue > 0) {
        highestIndex = Math.max(highestIndex, cellValue);
        pathIndexLocations[cellValue] = [row, col];
      }
    }
  }

  if (highestIndex < 2) return {};

  const lineClasses: { [pathIndex: number]: string } = {};

  for (let i = highestIndex; i > 0; i--) {
    if (i === highestIndex) {
      // find position relative to i - 1
      // only A (for ::after)
      lineClasses[i] = getClassesForCell(
        pathIndexLocations[i],
        pathIndexLocations[i - 1],
        null,
      );
    } else if (i === 1) {
      // find position relative to i + 1
      // only B (for ::before)
      lineClasses[i] = getClassesForCell(
        pathIndexLocations[i],
        null,
        pathIndexLocations[i + 1],
      );
    } else {
      // find position relative to i + 1 AND i - 1
      // A and B (::after and ::before)
      lineClasses[i] = getClassesForCell(
        pathIndexLocations[i],
        pathIndexLocations[i - 1],
        pathIndexLocations[i + 1],
      );
    }
  }

  return lineClasses;
};

export const positionMatches = (pos1: Position, pos2: Position) => {
  return pos1[0] === pos2[0] && pos1[1] === pos2[1];
};

export const isAdjacent = (pos1: Position, pos2: Position): boolean => {
  const [row1, col1] = pos1;
  const [row2, col2] = pos2;

  const rowDistance = Math.abs(row1 - row2);
  const colDistance = Math.abs(col1 - col2);

  return rowDistance <= 1 && colDistance <= 1 && (rowDistance + colDistance) !== 0;
};

export const violatesConsonantVowelPattern = (
  letter: string,
  row: number,
  col: number,
  vowelMatrix: number[][],
) => {
  return isLetterAVowel(letter) !== (vowelMatrix[row][col] === 0);
};

export const determineHighlights = (
  row: number,
  col: number,
  nextLetter: string,
  path: number[][],
  puzzleSize: number,
  vowelMatrix: number[][],
  hintPositions: {[key: string]: number},
  hintLetters: number[],
  enabledHints: number[],
  cluetext: string[],
  pathLength: number
) => {
    const highlights = getZeroMatrix(puzzleSize);

    // Build a map of enabled hint positions to their required path indices: {row_col: pathIndex}
    const enabledHintMap: {[key: string]: number} = {};
    Object.entries(hintPositions).forEach(([posKey, hintIndex]) => {
      const hintLetterPosition = hintLetters.indexOf(hintIndex);
      const isEnabled = hintLetterPosition !== -1 && enabledHints.includes(hintLetterPosition);
      if (isEnabled) {
        enabledHintMap[posKey] = hintIndex;
      }
    });

    // Check if nextLetter is an enabled hint letter (at current path position)
    const nextLetterHintIndex = hintLetters.indexOf(pathLength);
    const nextLetterIsEnabledHint = nextLetterHintIndex !== -1 && enabledHints.includes(nextLetterHintIndex);

    if (nextLetterIsEnabledHint) {
      // Next letter IS an enabled hint - ONLY highlight its designated cell if adjacent
      for (const [posKey, hintIndex] of Object.entries(enabledHintMap)) {
        if (hintIndex === pathLength) {
          const [r, c] = posKey.split('_').map(Number);
          const rowDistance = Math.abs(r - row);
          const colDistance = Math.abs(c - col);
          const isAdjacent = rowDistance <= 1 && colDistance <= 1 && (rowDistance + colDistance) !== 0;

          if (isAdjacent && path[r][c] === 0) {
            highlights[r][c] = 1;
          }
          break;
        }
      }
      return highlights; // Don't highlight any other cells
    }

    // Otherwise, use normal highlighting logic but exclude enabled hint positions
    directions.forEach(direction => {
      const newRow = row + direction[0];
      const newCol = col + direction[1];

      // out of bounds
      if (newRow < 0 || newCol < 0 || newRow > puzzleSize - 1 || newCol > puzzleSize - 1) return;
      // already filled
      if (path[newRow][newCol] > 0) return;

      // Skip this cell if it has an enabled hint preview
      const posKey = `${newRow}_${newCol}`;
      if (enabledHintMap[posKey]) return;

      const neighborSquareIsVowel = vowelMatrix[newRow][newCol] === 0;
      const nextLetterIsVowel = 'aeiouy'.includes(nextLetter);

      // if neighbor cell matches the consonant/vowel requirement for the next letter,
      // highlight it
      if (neighborSquareIsVowel === nextLetterIsVowel) {
        highlights[newRow][newCol] = 1;
      }
    });

    return highlights;
};

// operates on the draft from the pullstate store
export const resetPathwordData = (s: any) => {
  s.path = getZeroMatrix(4);
  s.lines = {};
  s.highlights = getZeroMatrix(4);
  s.pathLength = 0;
  s.pathHead = null;
  s.successAnimationGrid = getZeroMatrix(4);
};